<script setup>
import moment from 'moment'
import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useNotify } from '@/composables/notify'

import { useSaleStore } from '@/stores/sale.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { usePortalStore } from '@/stores/portal.store'

const props = defineProps({
  invoice: {
    type: Object,
    required: true
  },
  gridRef: {
    type: Object,
    required: false
  },
  refreshSummaryAsync: {
    type: Function,
    default: () => {}
  }
})

const i18n = getI18n()
const portalStore = usePortalStore()
const saleStore = useSaleStore()
const webSiteStore = useWebSiteStore()

const { notifySuccess, notifyError } = useNotify()

const isLoading = ref(false)
const isDownloading = ref(false)
const currentTime = ref(Date.now())

setInterval(() => { currentTime.value = Date.now() }, 1000)

const is_expiring = computed(() => {
  const updatedAt = props.invoice?.updated_at
  const electronicInvoiceProgressStatus = portalStore.electronicInvoiceProgressStatuses.find(i => i.code === props.invoice?.progress_status_code)
  const retentionNextMonth = electronicInvoiceProgressStatus?.retention_next_month
  const maxRetentionDays = electronicInvoiceProgressStatus?.max_retention_days

  if (!updatedAt || !maxRetentionDays) return false

  let startDate = new Date(updatedAt)
  if (retentionNextMonth) {
    const year = startDate.getFullYear()
    const month = startDate.getMonth() + 1
    startDate = new Date(year, month, 1)
  }

  const expirationDate = new Date(startDate)
  expirationDate.setDate(expirationDate.getDate() + maxRetentionDays)

  const today = new Date()
  const diffDays = Math.floor((expirationDate - today) / (1000 * 60 * 60 * 24)) // 1 day in milliseconds

  return diffDays < 3
})

const downloadElectronicInvoice = async() => {
  isDownloading.value = true
  await saleStore.api.downloadElectronicInvoicesAsync({ ids: [props.invoice?.id]}, null, { reloadAsync: () => props.gridRef?.instance?.refresh() })
  isDownloading.value = false
}

const updateElectronicInvoiceStatus = async() => {
  if (isLoading.value) return
  try {
    isLoading.value = true
    await saleStore.api.updateElectronicInvoiceStatusAsync(props.invoice?.id, null, { skipErrorMessage: true })
    notifySuccess(i18n.t('common.success_update'))
    try {
      await props.gridRef?.instance?.refresh()
    } finally {
      await props.refreshSummaryAsync()
    }
  } catch {
    notifyError(i18n.t('common.transmissionError'))
  } finally {
    isLoading.value = false
  }
}

const refreshButtonVisible = computed(() => {
  return props.invoice?.xml_file &&
    webSiteStore.user.has_electronic_invoice_transmission &&
    props.invoice?.progress_status_family_code === portalStore.electronicInvoiceFamiliesEnum.SENT
})

const refreshIntervalSeconds = 30

const remainingSeconds = computed(() => {
  if (!props.invoice?.updated_at) return 0
  const elapsedSeconds = moment(currentTime.value).diff(moment.utc(props.invoice?.updated_at.toString()), 'seconds')
  return Math.ceil(refreshIntervalSeconds - elapsedSeconds)
})

const refreshButtonDisabled = computed(() => {
  return !isLoading.value && remainingSeconds.value > 0
})
</script>

<template>
  <div>
    <q-btn
      v-if="props.invoice?.xml_file"
      icon="mdi-download"
      size="sm"
      class="dx-link"
      round
      flat
      :color="props.invoice?.is_valid === false ? 'red' : 'primary'"
      :disable="isDownloading"
      @click="downloadElectronicInvoice"
    >
      <q-tooltip>
        {{ props.invoice?.is_valid === false ? i18n.t('error.validation.electronic_invoice.invalid_generated_xml') : i18n.t('common.download') }}
      </q-tooltip>
    </q-btn>
    <q-btn
      v-if="refreshButtonVisible"
      :loading="isLoading"
      loading-spinner="QSpinnerGears"
      size="sm"
      round
      flat
      :disable="refreshButtonDisabled"
      @click="updateElectronicInvoiceStatus"
    >
      <q-circular-progress
        v-if="refreshButtonDisabled"
        :value="remainingSeconds"
        show-value
        reverse
        color="blue"
        size="1.25rem"
        :thickness="0.22"
        track-color="grey-3"
        :min="0"
        :max="refreshIntervalSeconds"
      >
        <span style="font-size: 0.6rem; padding-top: .1rem;">{{ remainingSeconds }}</span>
      </q-circular-progress>
      <q-icon
        v-else
        name="mdi-refresh"
      />
      <q-tooltip v-if="refreshButtonDisabled">
        {{ i18n.t('common.electronicInvoice.refreshWaitingSeconds', { seconds: remainingSeconds }) }}
      </q-tooltip>
      <q-tooltip v-else>
        {{ i18n.t('common.electronicInvoice.refreshInvoiceStatus') }}
      </q-tooltip>
    </q-btn>
    <q-icon
      v-if="is_expiring"
      class="q-ml-sm"
      name="mdi-alert"
      color="orange"
    >
      <q-tooltip>
        {{ i18n.t('common.electronicInvoiceExpiringMessage') }}
      </q-tooltip>
    </q-icon>
  </div>
</template>

<style scoped>
</style>
